import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { map, filter, memoize, assign, concat, toPairs } from 'lodash-es';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { mountComponentByClassWithData } from '../../common/mount-helpers';
import { propertyTypeOptions, acquireAvailabilityOptionsFilter } from '../../common/listing-types';
import { FiltersMobile } from './filters-mobile';
import { Listings } from './listings';
import { FilterBar } from './filter-bar';
const sortOptions = [
    { value: 'price-desc', label: 'Price High to Low' },
    { value: 'price-asc', label: 'Price Low to High' },
];
function AcquirePageListingsSearcher(props) {
    const { apiNonce, itemsCountPerPage, regions, filters } = props;
    const listingsRef = useRef(null);
    /* Sort listing config */
    const [sort, setSort] = useState('');
    /* Pagination configuration */
    const [totalItemsCount, setTotalItemsCount] = useState(3);
    /* Listings configuration */
    const [listings, setListings] = useState([]);
    const [listingsState, setListingsState] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersData, setFiltersData] = useState({ page: currentPage.toString(), ...filters });
    const updateFiltersData = useCallback((filters) => {
        setFiltersData(assign({}, filtersData, filters, { page: '1' }));
    }, [setFiltersData, filtersData]);
    const updateFilter = useCallback(memoize((filter) => (value) => {
        if (filtersData[filter] !== value) {
            setFiltersData(assign({}, filtersData, { [filter]: value, page: '1' }));
        }
    }), [setFiltersData, filtersData]);
    const updatePage = useCallback((page) => {
        setFiltersData(assign({}, filtersData, { 'page': String(page) }));
    }, [setFiltersData, filtersData]);
    /* Search configuration */
    const search = useMemo(() => {
        const query = filter(toPairs(filtersData), ([name, value]) => {
            if (!value) {
                return false;
            }
            /* validate numeric range fields */
            const numericFields = ['price', 'sqft'];
            if (numericFields.includes(name)) {
                const [start, end] = value.split(',');
                return Boolean(start && end);
            }
            return true;
        });
        const sortParts = sort ? sort.split('-') : [];
        const sortQuery = sortParts.length === 2 ? [['sort', sortParts[0]], ['order', sortParts[1].toUpperCase()]] : [];
        const queryString = map(concat(query, sortQuery), ([name, value]) => `${name}=${value}`).join('&');
        return queryString;
    }, [filtersData, sort]);
    const [debouncedSearch] = useDebounce(search, 200);
    /* Fetch listings */
    useEffect(() => {
        (async () => {
            const url = `/wp-json/macleod/v1/listings?listing_page=acquire${debouncedSearch ? '&' + debouncedSearch : ''}&order_by=price`;
            try {
                setListingsState('loading');
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-WP-Nonce': apiNonce,
                    },
                });
                if (!response.ok) {
                    throw new Error('HTTP status ' + response.status);
                }
                const result = await response.json();
                const acquire = result.listings.filter((listing) => listing.listing_page == 'acquire');
                setListings(acquire);
                setTotalItemsCount(result.numMaxPages * itemsCountPerPage);
                setListingsState('success');
                setErrorMessage('');
                if (parseInt(filtersData.page) !== currentPage && listingsRef.current) {
                    const yOffset = -200;
                    // @ts-ignore
                    const y = listingsRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                setCurrentPage(parseInt(filtersData.page));
            }
            catch (error) {
                setListings([]);
                setListingsState('error');
                setErrorMessage(error.message || 'Unexpected Error');
            }
        })();
    }, [debouncedSearch]);
    return (_jsxs("div", { className: "acquire-page-listings-searcher", children: [_jsx(FiltersMobile, { filtersData: filtersData, updateFiltersData: updateFiltersData, sort: sort, updateSort: setSort, sortOptions: sortOptions, propertyTypeOptions: propertyTypeOptions, availabilityOptions: acquireAvailabilityOptionsFilter, locationData: regions }), _jsx(FilterBar, { filtersData: filtersData, updateFiltersData: updateFiltersData, propertyTypeOptions: propertyTypeOptions, availabilityOptions: acquireAvailabilityOptionsFilter, locationData: regions }), _jsxs("div", { className: "acquire-page-listings-searcher-content", ref: listingsRef, children: [listingsState === 'loading' ? _jsx("div", { className: "loading-message", children: _jsx("span", { className: "square-loader", children: _jsx("span", { className: "square-loader-inner" }) }) }) : null, listingsState === 'error' ? _jsx("div", { className: "error-message", children: errorMessage }) : null, listingsState === 'success' ?
                        _jsx(Listings, { listings: listings, page: filtersData['page'] ? Number(filtersData['page']) : 1, itemsCountPerPage: itemsCountPerPage, totalItemsCount: totalItemsCount, updatePage: updatePage }) :
                        null] })] }));
}
window.addEventListener('load', () => {
    mountComponentByClassWithData('acquire-page-listings-searcher-wrapper', (props) => _jsx(AcquirePageListingsSearcher, { ...props }));
});
